<template>
  <!-- Payment Summary Block with purpose-driven header -->
  <div>
    <!-- Purpose Header: icon + title + optional step badge -->
    <header class="mb-4">
      <div class="flex items-center gap-2">
        <component :is="ReceiptPercentIcon" class="h-6 w-6 text-primary" />
        <h3 class="text-lg font-bold text-primary">
          {{ $t("pricing.breakdownTitle") }}
        </h3>
      </div>
      <p class="text-xs mt-1 text-base-content/60">
        {{ $t("pricing.breakdownSubtitle") }}
      </p>
    </header>

    <table class="w-full text-base divide-y divide-base-200">
      <tbody>
        <!-- One-time -->
        <tr v-if="hasOneTime">
          <td class="py-2">{{ $t("pricing.summary.oneTimeServices") }}</td>
          <td class="py-2 text-right font-mono">{{ formattedOneTime }}</td>
        </tr>

        <!-- Monthly -->
        <tr v-if="hasMonthly">
          <td class="py-2">{{ $t("pricing.summary.monthlyBilledPlans") }}</td>
          <td class="py-2 text-right font-mono">{{ formattedMonthlyRow }}</td>
        </tr>

        <!-- Yearly -->
        <tr v-if="hasYearly">
          <td class="py-2">{{ $t("pricing.summary.yearlyBilledPlans") }}</td>
          <td class="py-2 text-right font-mono">{{ formattedYearlyRow }}</td>
        </tr>

        <!-- VAT combined -->
        <tr>
          <td class="py-2">{{ $t("pricing.summary.vat") }} ({{ (taxRate * 100).toFixed(0) }}%)<span v-if="taxRate === 0">*</span></td>
          <td class="py-2 text-right font-mono">{{ formattedCombinedVAT }}</td>
        </tr>

        <!-- 💰 Total -->
        <tr class="bg-primary/10 font-semibold">
          <td class="py-3">{{ $t("pricing.summary.totalDueToday") }}</td>
          <td class="py-3 text-right font-mono text-lg">{{ formattedTotalDueNow }}</td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script setup lang="ts">
import { defineProps, computed } from "vue";
import { ReceiptPercentIcon } from "@heroicons/vue/24/solid";
import { createCurrencyFormatter } from "@/utils/formatCurrency";
import { BillingInterval } from "../../types/BillingInterval";

const props = defineProps<{
  formattedOneTime: string;
  formattedMonthlyRow: string;
  formattedYearlyRow: string;
  formattedTotalDueNow: string;
  formattedTaxOnOneTime: string;
  formattedTaxOnMonthly: string;
  formattedTaxOnYearly: string;
  hasOneTime: boolean;
  hasMonthly: boolean;
  hasYearly: boolean;
  taxRate: number;
  billingInterval: BillingInterval;
}>();

// Currency formatter
const format = createCurrencyFormatter({ locale: "nl-NL", currency: "EUR" });

// Combined VAT Calculation
const formattedCombinedVAT = computed(() => {
  const parseCurrency = (value: string) => parseFloat(value.replace(/[€\s]/g, "").replace(",", "."));

  const totalVAT =
    parseCurrency(props.formattedTaxOnOneTime) +
    parseCurrency(props.formattedTaxOnMonthly || "0") +
    parseCurrency(props.formattedTaxOnYearly || "0");

  return format(totalVAT).formatted;
});
</script>
